import React, { useState } from 'react'
import { Link, NavLink } from 'react-router-dom'

const NAV_LINKS = [
  ['/', 'Home'],
  ['/intake-ai', 'Intake AI'],
  ['/advertising', 'Advertising'],
  ['/board', 'Board Members'],
  ['/pricing', 'Pricing'],
  ['/contact', 'Contact'],
]

export function Wordmark({ light = false }) {
  return (
    <Link to="/" aria-label="LeaseCheck home" className="group flex items-center gap-3">
      <img src="/attorney/LeaseCheck%20Logo%20resized.png" alt="" aria-hidden="true" className="h-[68px] w-auto object-contain" />
      <span className={`text-2xl font-bold tracking-tight ${light ? 'text-white' : 'text-emerald-600'}`}>LeaseCheck</span>
      <span className="hidden rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800 sm:inline-block">for Lawyers</span>
    </Link>
  )
}

export function AttorneyLayout({ children }) {
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <div className="lc-page min-h-screen bg-white font-sans text-slate-900">
      <style>{`
        @keyframes lc-rise { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: none; } }
        .lc-rise { animation: lc-rise .7s cubic-bezier(.22,.61,.36,1) both; }
        @media (prefers-reduced-motion: reduce) { .lc-rise { animation: none; } }
        .lc-page a:focus-visible, .lc-page button:focus-visible, .lc-page input:focus-visible, .lc-page select:focus-visible, .lc-page textarea:focus-visible { outline: 2px solid #10b981; outline-offset: 2px; border-radius: 0.5rem; }
      `}</style>

      <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/90 backdrop-blur-md">
        <nav aria-label="Primary" className="mx-auto flex max-w-7xl items-center justify-between gap-5 px-5 py-3.5">
          <Wordmark />
          <div className="hidden items-center gap-5 text-sm font-medium text-slate-600 lg:flex">
            {NAV_LINKS.map(([to, label]) => <NavLink key={to} to={to} end className={({ isActive }) => `transition ${isActive ? 'font-semibold text-emerald-700 underline decoration-2 decoration-emerald-500 underline-offset-8' : 'text-slate-600 hover:text-emerald-700'}`}>{label}</NavLink>)}
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" className="hidden text-sm font-semibold text-slate-700 transition hover:text-slate-900 sm:block">Sign in</Link>
            <Link to="/pricing" className="hidden rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:from-emerald-700 hover:to-teal-700 sm:inline-flex">Get started</Link>
            <button type="button" onClick={() => setMenuOpen((open) => !open)} aria-label={menuOpen ? 'Close menu' : 'Open menu'} aria-expanded={menuOpen} aria-controls="lc-mobile-menu" className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-slate-700 transition hover:bg-slate-100 lg:hidden">
              <svg viewBox="0 0 24 24" aria-hidden="true" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {menuOpen ? <path d="M6 6l12 12M18 6 6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
              </svg>
            </button>
          </div>
        </nav>
        {menuOpen && (
          <div id="lc-mobile-menu" className="border-t border-slate-200 bg-white lg:hidden">
            <div className="mx-auto flex max-w-7xl flex-col px-5 py-2">
              {NAV_LINKS.map(([to, label]) => <NavLink key={to} to={to} end onClick={() => setMenuOpen(false)} className={({ isActive }) => `border-b border-slate-100 py-3 text-sm font-medium ${isActive ? 'font-semibold text-emerald-700' : 'text-slate-700'}`}>{label}</NavLink>)}
              <Link to="/login" onClick={() => setMenuOpen(false)} className="py-3 text-sm font-semibold text-emerald-700">Sign in</Link>
              <Link to="/pricing" onClick={() => setMenuOpen(false)} className="my-2 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 px-4 py-3 text-center text-sm font-bold text-white">Get started</Link>
            </div>
          </div>
        )}
      </header>

      <main>{children}</main>

      <footer className="border-t border-slate-200 bg-slate-950 text-white">
        <div className="mx-auto max-w-7xl px-5 py-14">
          <div className="grid gap-10 md:grid-cols-3">
            <div>
              <Wordmark light />
              <p className="mt-4 max-w-sm text-sm leading-relaxed text-white">Intake and advertising tools built for Colorado real estate law practices.</p>
              <p className="mt-3 text-sm font-semibold text-emerald-300">LeaseCheck Attorney Network</p>
            </div>
            <div>
              <h2 className="text-sm font-bold uppercase tracking-widest text-emerald-300">Company</h2>
              <div className="mt-4 grid gap-3 text-sm text-slate-300">
                {NAV_LINKS.map(([to, label]) => <Link key={to} to={to} className="transition hover:text-white">{label}</Link>)}
              </div>
            </div>
            <div>
              <h2 className="text-sm font-bold uppercase tracking-widest text-emerald-300">Products</h2>
              <div className="mt-4 grid gap-3 text-sm text-slate-300">
                <a href="https://intakeai.coloradoleasecheck.com/" target="_blank" rel="noopener noreferrer" className="transition hover:text-white">Intake AI</a>
                <Link to="/advertising" className="transition hover:text-white">Client Advertising Platform</Link>
                <a href="https://coloradoleasecheck.com/" target="_blank" rel="noopener noreferrer" className="transition hover:text-white">Colorado LeaseCheck for Renters</a>
                <a href="https://landlords.coloradoleasecheck.com/" target="_blank" rel="noopener noreferrer" className="transition hover:text-white">Colorado LeaseCheck for Landlords</a>
                <a href="https://dewy-beatem-and-howe.com/" target="_blank" rel="noopener noreferrer" className="transition hover:text-white">DB&amp;H Demo Website</a>
              </div>
            </div>
          </div>
          <div className="mt-12 border-t border-slate-800 pt-6 text-xs leading-relaxed text-white">
            <p>The Client Advertising Platform provides attorney visibility and profile placement to Colorado renters and landlords. LeaseCheck does not provide legal advice, charge per inquiry, or split legal fees — any legal advice or attorney-client relationship occurs only between the attorney and prospective client directly. Advertising does not constitute a solicitation or an endorsement of any attorney's services.</p>
            <p className="mt-3">© {new Date().getFullYear()} LeaseCheck. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
